import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
const initialState = {
    configurations: [],
    modules: [],
    users: [],
    loading: false,
    error: null,
    saving: false,
    saveError: null,
    deleting: false,
    deleteError: null,
};
// ─── Request helpers ────────────────────────────────────────────────────────
const BASE = "/api/email-configurations";
const authHeaders = (token) => ({
    Authorization: `Bearer ${token}`,
    "x-module-key": "email_configuration",
});
const extractErrorMessage = (response, fallback) => response?.data?.message || fallback;
const isSuccessResponse = (response) => !!response && "data" in response && response.data?.success === true;
// ─── Thunks ─────────────────────────────────────────────────────────────────
// Single-call bootstrap: configurations + modules + users in one HTTP round trip.
// Replaces the previous trio of fetchEmailConfigurations / fetchModules /
// fetchDesignationWiseUsers when this page is opened.
export const fetchEmailConfigurationBootstrap = createAsyncThunk("emailConfiguration/bootstrap", async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("GET", `${BASE}/bootstrap`, undefined, authHeaders(token));
        if (isSuccessResponse(response)) {
            const data = response.data.data || {};
            return {
                configurations: data.configurations || [],
                modules: data.modules || [],
                users: data.users || [],
            };
        }
        return rejectWithValue(extractErrorMessage(response, "Failed to load email configuration data"));
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message || error?.message || "Failed to load email configuration data");
    }
});
// Fetch only the configurations list (used after the page is already bootstrapped).
export const fetchEmailConfigurations = createAsyncThunk("emailConfiguration/fetchAll", async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("GET", BASE, undefined, authHeaders(token));
        if (isSuccessResponse(response)) {
            return response.data.data || [];
        }
        return rejectWithValue(extractErrorMessage(response, "Failed to fetch email configurations"));
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message || error?.message || "Failed to fetch email configurations");
    }
});
export const createEmailConfiguration = createAsyncThunk("emailConfiguration/create", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("POST", BASE, payload, authHeaders(token));
        if (isSuccessResponse(response)) {
            toast.success("Email configuration created successfully");
            return response.data.data;
        }
        const errorMsg = extractErrorMessage(response, "Failed to create email configuration");
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
    }
    catch (error) {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to create email configuration";
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
    }
});
export const updateEmailConfiguration = createAsyncThunk("emailConfiguration/update", async ({ id, silent, ...payload }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("PUT", `${BASE}/${id}`, payload, authHeaders(token));
        if (isSuccessResponse(response)) {
            if (!silent)
                toast.success("Email configuration updated successfully");
            return response.data.data;
        }
        const errorMsg = extractErrorMessage(response, "Failed to update email configuration");
        if (!silent)
            toast.error(errorMsg);
        return rejectWithValue(errorMsg);
    }
    catch (error) {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to update email configuration";
        if (!silent)
            toast.error(errorMsg);
        return rejectWithValue(errorMsg);
    }
});
// Bulk update — replaces N sequential PUTs from the User Assignment drawer.
export const bulkUpdateEmailConfigurations = createAsyncThunk("emailConfiguration/bulkUpdate", async (updates, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("PUT", `${BASE}/bulk`, { updates }, authHeaders(token));
        if (isSuccessResponse(response)) {
            toast.success("Email configurations updated successfully");
            return response.data.data || [];
        }
        const errorMsg = extractErrorMessage(response, "Failed to update email configurations");
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
    }
    catch (error) {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to update email configurations";
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
    }
});
export const fetchUserRecipientRoles = createAsyncThunk("emailConfiguration/fetchUserRecipientRoles", async (userId, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("GET", `${BASE}/user-roles/${userId}`, undefined, authHeaders(token));
        if (isSuccessResponse(response)) {
            return response.data.data || [];
        }
        return rejectWithValue(extractErrorMessage(response, "Failed to fetch user recipient roles"));
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message || error?.message || "Failed to fetch user recipient roles");
    }
});
export const deleteEmailConfiguration = createAsyncThunk("emailConfiguration/delete", async (id, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("DELETE", `${BASE}/${id}`, null, authHeaders(token));
        if (isSuccessResponse(response)) {
            toast.success("Email configuration deleted successfully");
            return id;
        }
        const errorMsg = extractErrorMessage(response, "Failed to delete email configuration");
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
    }
    catch (error) {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to delete email configuration";
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
    }
});
// ─── Slice ──────────────────────────────────────────────────────────────────
// Replaces the matching config in `configurations` (by _id). If it doesn't
// exist, appends it. Used by the create/update fulfilled reducers so we never
// need a second round-trip just to refresh the list.
const upsertConfiguration = (list, config) => {
    const index = list.findIndex((c) => c._id === config._id);
    if (index === -1)
        return [config, ...list];
    const next = [...list];
    next[index] = config;
    return next;
};
const emailConfigurationSlice = createSlice({
    name: "emailConfiguration",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Bootstrap
            .addCase(fetchEmailConfigurationBootstrap.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchEmailConfigurationBootstrap.fulfilled, (state, action) => {
            state.loading = false;
            state.configurations = action.payload.configurations;
            state.modules = action.payload.modules;
            state.users = action.payload.users;
            state.error = null;
        })
            .addCase(fetchEmailConfigurationBootstrap.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to load email configuration data";
        })
            // Fetch all (used when re-syncing just the list)
            .addCase(fetchEmailConfigurations.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchEmailConfigurations.fulfilled, (state, action) => {
            state.loading = false;
            state.configurations = action.payload;
            state.error = null;
        })
            .addCase(fetchEmailConfigurations.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch email configurations";
        })
            // Create — append the returned config to local state (no refetch)
            .addCase(createEmailConfiguration.pending, (state) => {
            state.saving = true;
            state.saveError = null;
        })
            .addCase(createEmailConfiguration.fulfilled, (state, action) => {
            state.saving = false;
            state.saveError = null;
            state.configurations = upsertConfiguration(state.configurations, action.payload);
        })
            .addCase(createEmailConfiguration.rejected, (state, action) => {
            state.saving = false;
            state.saveError = action.payload || "Failed to create email configuration";
        })
            // Update — replace the existing config in local state (no refetch)
            .addCase(updateEmailConfiguration.pending, (state) => {
            state.saving = true;
            state.saveError = null;
        })
            .addCase(updateEmailConfiguration.fulfilled, (state, action) => {
            state.saving = false;
            state.saveError = null;
            state.configurations = upsertConfiguration(state.configurations, action.payload);
        })
            .addCase(updateEmailConfiguration.rejected, (state, action) => {
            state.saving = false;
            state.saveError = action.payload || "Failed to update email configuration";
        })
            // Bulk update — replace each returned config in local state
            .addCase(bulkUpdateEmailConfigurations.pending, (state) => {
            state.saving = true;
            state.saveError = null;
        })
            .addCase(bulkUpdateEmailConfigurations.fulfilled, (state, action) => {
            state.saving = false;
            state.saveError = null;
            state.configurations = action.payload.reduce(upsertConfiguration, state.configurations);
        })
            .addCase(bulkUpdateEmailConfigurations.rejected, (state, action) => {
            state.saving = false;
            state.saveError = action.payload || "Failed to update email configurations";
        })
            // Delete — remove from local state
            .addCase(deleteEmailConfiguration.pending, (state) => {
            state.deleting = true;
            state.deleteError = null;
        })
            .addCase(deleteEmailConfiguration.fulfilled, (state, action) => {
            state.deleting = false;
            state.deleteError = null;
            state.configurations = state.configurations.filter((c) => c._id !== action.payload);
        })
            .addCase(deleteEmailConfiguration.rejected, (state, action) => {
            state.deleting = false;
            state.deleteError = action.payload || "Failed to delete email configuration";
        });
    },
});
// ─── Selectors ──────────────────────────────────────────────────────────────
export const selectEmailConfigurations = (state) => state.emailConfiguration.configurations;
export const selectEmailConfigModules = (state) => state.emailConfiguration.modules;
export const selectEmailConfigUsers = (state) => state.emailConfiguration.users;
export const selectEmailConfigurationLoading = (state) => state.emailConfiguration.loading;
export const selectEmailConfigurationError = (state) => state.emailConfiguration.error;
export const selectEmailConfigurationSaving = (state) => state.emailConfiguration.saving;
export const selectEmailConfigurationSaveError = (state) => state.emailConfiguration.saveError;
export const selectEmailConfigurationDeleting = (state) => state.emailConfiguration.deleting;
export const selectEmailConfigurationDeleteError = (state) => state.emailConfiguration.deleteError;
export default emailConfigurationSlice.reducer;
